/**
 * Jettison, the HUD verb (M8 TR-8b2; M6 spec §6.4.2, §6.4.9; D-523, D-524; the career in the Reach only). Once the
 * career feature (order 150) has provided `m2` and the Reach runs, binds the jettison bridge hook
 * (`bridge/m2/encounters.ts attachJettison`, loaded lazily with the Reach) the way `features/bridgeRaiders` binds its
 * hook, and draws three lines on `#hud`:
 *
 * - **The offer** (during a tug, or while a failed tug's group lives): "X JETTISON 25% · [50%] · ALL: ...". Tap
 *   `jettison.json keys` (X) or the pad button (R3 alone) to cycle the share; hold it `holdS` to drop it.
 * - **The pod tag** while a dropped pod is still the raiders': "RAIDER-CLAIMED 1:45".
 * - **A notice** for `noticeS`: what dropped, a refusal, or "You took the raiders' pod. They're coming back for it."
 *
 * Per frame it reads the event rings (allocation-free) and rewrites a line only when its text changes; strings are
 * built only on a change (a share pick, a new claim second, a notice).
 */
import { FEATURE_ORDER, type FrameInfo, type GameContext, type GameFeature } from '../../app/feature'
import type { JettisonHook, JettisonNotice, JettisonOutcome, JettisonPhase } from '../../bridge/m2/encounters'
import type { CareerService } from '../../career/flow'
import { loadJettisonConfig, type JettisonConfig } from '../../career/jettison'
import { GOOD_NAMES } from '../../career/ops'
import { GOOD_IDS } from '../../career/parts'
import { formatTonnes } from '../../career/result'
import type { CargoContents } from '../../career/types'
import { logger } from '../../core/logger'
import { WARE_IDS, type M2Data } from '../../data/m2Schemas'
import type { M2Runtime } from '../../space/contracts'

/** After M1's HUD (500), which rewrites `#hud` on mount, and the mission HUD (537); before station services (540). */
const ORDER = FEATURE_ORDER.storyHud + 1
/** The pad button the cruise chord pairs with R3 (`reach-controls.json gamepad.cruise.buttons`): R3 alone only. */
const PAD_L3 = 10

/** The `m2` service with its dev-command write hook (the career flow's `CareerService`). */
function hasWriteHook(m2: M2Runtime): m2 is CareerService {
  return 'applyDevCommand' in m2 && typeof m2.applyDevCommand === 'function'
}

/** The share label: "25%", or `all` for everything. */
export function shareLabel(share: number, cfg: JettisonConfig): string {
  return share >= 1 ? cfg.text.all : `${Math.round(share * 100)}%`
}

/** The offer line with the picked share in brackets. */
export function offerText(cfg: JettisonConfig, picked: number, key: string): string {
  const shares = cfg.shares.map((s, i) => (i === picked ? `[${shareLabel(s, cfg)}]` : shareLabel(s, cfg))).join(' · ')
  return `${cfg.text.offer.replace('{key}', key).replace('{shares}', shares)} ${cfg.text.offerHint}`
}

/** "12.0 t Platinum, 2 Hull plating": the pod's wares in tonnes and its goods in units. */
export function contentsList(contents: CargoContents, wares: M2Data['wares']): string {
  const out: string[] = []
  for (const w of WARE_IDS) {
    const t = contents.wares[w]
    if (t > 0) out.push(`${formatTonnes(t)} t ${wares.wares.find((x) => x.id === w)?.name ?? w}`)
  }
  for (const g of GOOD_IDS) {
    const q = contents.goods?.[g] ?? 0
    if (q > 0) out.push(`${Number.isInteger(q) ? q : q.toFixed(1)} ${GOOD_NAMES[g]}`)
  }
  return out.join(', ')
}

/** "1:45" from seconds (rounded up). */
export function clock(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** The notice line of a jettison outcome or a hook notice. */
export function noticeText(cfg: JettisonConfig, n: JettisonNotice | JettisonOutcome, wares: M2Data['wares']): string | null {
  if ('kind' in n) return n.kind === 'recovered' ? cfg.text.recovered : cfg.text.dropped.replace('{list}', contentsList(n.contents, wares))
  if (n.ok) return null
  if (n.reason === 'nothing') return cfg.text.nothing
  if (n.reason === 'tooSmall') return cfg.text.tooSmall
  if (n.reason === 'failed') return cfg.text.failed
  return null
}

interface View {
  root: HTMLDivElement
  offer: HTMLDivElement
  pod: HTMLDivElement
  notice: HTMLDivElement
}

function line(root: HTMLElement, testId: string, color: string): HTMLDivElement {
  const el = document.createElement('div')
  el.dataset.testid = testId
  el.style.cssText = `margin-top:4px;color:${color};text-shadow:0 0 4px rgba(0,0,0,0.9);display:none`
  root.appendChild(el)
  return el
}

function createView(host: HTMLElement): View {
  const root = document.createElement('div')
  root.className = 'jettison-hud'
  root.style.cssText =
    'position:absolute;left:50%;bottom:24%;transform:translateX(-50%);text-align:center;font:600 13px/1.35 ui-monospace,monospace;letter-spacing:0.04em;pointer-events:none;max-width:min(92vw,720px)'
  host.appendChild(root)
  return { root, offer: line(root, 'jettison-offer', '#ffcf6b'), pod: line(root, 'jettison-pod', '#ff7a6b'), notice: line(root, 'jettison-notice', '#d8e2ec') }
}

/** Shows `text` on `el` (null hides it); writes the DOM only on a change. */
function show(el: HTMLDivElement, text: string | null): void {
  const display = text === null ? 'none' : 'block'
  if (el.style.display !== display) el.style.display = display
  if (text !== null && el.textContent !== text) el.textContent = text
}

interface State {
  hook: JettisonHook
  view: View
  cfg: JettisonConfig
  m2: M2Runtime
  picked: number
  heldS: number
  fired: boolean
  /** A key press seen since the last frame (set by keydown): a tap shorter than one frame still counts. */
  pressed: boolean
  onKeyDown: (e: KeyboardEvent) => void
  offerOpen: boolean
  offerLine: string | null
  podSecond: number
  podLine: string | null
  notice: string | null
  noticeLeftS: number
}

let state: State | null = null

/** The verb's input: the key or the pad button (R3 without L3) held now. */
function inputHeld(ctx: GameContext, cfg: JettisonConfig): boolean {
  const keys = ctx.input.keys
  for (let i = 0; i < cfg.keys.length; i++) if (keys.has(cfg.keys[i])) return true
  const pad = ctx.input.pad
  if (pad === null) return false
  return pad.buttons[cfg.padButton]?.pressed === true && pad.buttons[PAD_L3]?.pressed !== true
}

function setNotice(s: State, text: string | null): void {
  if (text === null) return
  s.notice = text
  s.noticeLeftS = s.cfg.noticeS
}

function frameState(s: State, ctx: GameContext, f: FrameInfo): void {
  const { hook, cfg, view } = s
  hook.step()
  const open = !f.paused && hook.phase() !== 'none'
  if (open !== s.offerOpen) {
    s.offerOpen = open
    s.offerLine = null
    s.heldS = 0
    s.fired = false
  }
  // The career flow drops the R3 tool-cycle tap while the offer owns R3.
  s.m2.space.input.cycleSuppressed = open
  // Held now, or pressed since the last frame: a slow frame (e.g. the raiders' first draw) can span a whole tap,
  // and a level-only read would lose it. One latched frame adds at most one capped dt, below `holdS`, so it reads as a tap.
  const down = s.pressed || inputHeld(ctx, cfg)
  s.pressed = false
  if (open) {
    if (down) {
      s.heldS += f.dt
      if (!s.fired && s.heldS >= cfg.holdS) {
        s.fired = true
        const out = hook.jettison(cfg.shares[s.picked])
        if (!out.ok) setNotice(s, noticeText(cfg, out, s.m2.data.wares))
      }
    } else {
      if (s.heldS > 0 && !s.fired) {
        s.picked = (s.picked + 1) % cfg.shares.length
        s.offerLine = null
      }
      s.heldS = 0
      s.fired = false
    }
    s.offerLine ??= offerText(cfg, s.picked, cfg.keys[0].replace(/^Key/, ''))
  }
  show(view.offer, s.offerOpen ? s.offerLine : null)

  // The pod tag: the soonest claim still running (or the claimed pod after its clock: still the raiders').
  const claims = hook.claims
  if (claims.length === 0) {
    s.podSecond = -1
    s.podLine = null
  } else {
    const left = claims[0].claimUntil - s.m2.space.mirror.gameTime
    const second = left > 0 ? Math.ceil(left) : 0
    if (second !== s.podSecond || s.podLine === null) {
      s.podSecond = second
      s.podLine = second > 0 ? cfg.text.podTag.replace('{time}', clock(second)) : cfg.text.podTagOpen
    }
  }
  show(view.pod, s.podLine)

  if (s.notice !== null) {
    s.noticeLeftS -= f.dt
    if (s.noticeLeftS <= 0) s.notice = null
  }
  show(view.notice, s.notice)
}

/** The open offer for the dashboard's Ship › Cargo Jettison (card C2): 'none' while the verb is not running. */
export function jettisonPhase(): JettisonPhase {
  return state === null ? 'none' : state.hook.phase()
}

/**
 * The dashboard's Jettison button (card C2): drops `share` through the same hook as the HUD verb (X held). A refusal
 * shows as the HUD notice; null when the verb is not running.
 */
export function jettisonFromDashboard(share: number): JettisonOutcome | null {
  if (state === null) return null
  const out = state.hook.jettison(share)
  if (!out.ok) setNotice(state, noticeText(state.cfg, out, state.m2.data.wares))
  return out
}

const feature: GameFeature = {
  name: 'jettison',
  order: ORDER,
  async init(ctx: GameContext): Promise<void> {
    const reach = ctx.reach
    const m2 = ctx.services.maybe('m2')
    if (reach === null || m2 === undefined || !hasWriteHook(m2)) return
    try {
      const [{ attachJettison }, { careerAccess }] = await Promise.all([import('../../bridge/m2/encounters'), import('../../bridge/m2/career')])
      const cfg = loadJettisonConfig()
      const access = careerAccess(m2)
      const view = createView(ctx.hudRoot)
      const s: Partial<State> = {}
      const hook = attachJettison({
        sim: ctx.sim,
        reach,
        career: access,
        m2: m2.data,
        salvage: m2.space.salvage,
        spaceEvents: m2.space.events,
        gameTime: () => m2.space.mirror.gameTime,
        config: cfg,
        notify: (n) => {
          if (state !== null) setNotice(state, noticeText(cfg, n, m2.data.wares))
        },
      })
      const onKeyDown = (e: KeyboardEvent): void => {
        if (!e.repeat && cfg.keys.includes(e.code) && state !== null) state.pressed = true
      }
      window.addEventListener('keydown', onKeyDown)
      Object.assign(s, { hook, view, cfg, m2, picked: 0, heldS: 0, fired: false, pressed: false, onKeyDown, offerOpen: false, offerLine: null, podSecond: -1, podLine: null, notice: null, noticeLeftS: 0 })
      state = s as State
    } catch (err: unknown) {
      logger.warn('jettison: the HUD verb did not start', err)
    }
  },
  frame(ctx: GameContext, f: FrameInfo): void {
    if (state !== null) frameState(state, ctx, f)
  },
  dispose(): void {
    if (state !== null) {
      state.m2.space.input.cycleSuppressed = false
      state.hook.dispose()
      state.view.root.remove()
      window.removeEventListener('keydown', state.onKeyDown)
    }
    state = null
  },
}

export default feature
